import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { SLICE_DISTRICT } from '../config';
import { TOWN_LOCATIONS } from '../rooms';
import { brickTexture, litWindowsTexture, withRepeat } from '../utils/facadeTexture';
import { useOptionalTexture } from '../utils/useOptionalTexture';
import Lowrider from './Lowrider';
import Park from './Park';

// ── HypeTunes Hip-Hop Town ──────────────────────────────────────────────────
// A deep city block, not a row of boxes. Two venues face the main street; two
// sit back — the Listening Room down a wet alley, the Concert Hall at the far
// corner. Filler storefronts, rooftop clutter, fire escapes, wires and neon
// build depth. Camera approaches from −z, so facades face −z and the block
// recedes into +z. Venue positions mirror TOWN_LOCATIONS (map + camera stay
// in sync). It's still procedural — the swap target is splat/GLTF art later.

const PALETTE = SLICE_DISTRICT.palette;

// One streetlight whose glow pulses on beat; `night` (0..1) brings practicals up.
function Streetlight({ position, beatPhase, energy, night = 0 }) {
  const bulb = useRef();
  const light = useRef();
  useFrame(() => {
    // ONE smooth swell per beat (cos = a single hump; |sin| double-flashed, which
    // read as fast flashing). Peaks on the downbeat, eases to a trough mid-beat —
    // a heartbeat, not a strobe. Depth is gentle, a touch deeper on louder songs.
    const beat = 0.5 + 0.5 * Math.cos(beatPhase * Math.PI * 2); // 1 on the beat → 0 between
    const swing = 0.55 + energy * 0.35;
    const duty = 0.25 + night * 1.1;
    // The bulb itself breathes on the beat so it's readable even in daylight; the
    // cast practical light stays day/night-gated (no floodlighting the street at noon).
    if (bulb.current) bulb.current.material.emissiveIntensity = 0.9 + beat * swing * 1.3;
    if (light.current) light.current.intensity = 7 * duty * (0.82 + beat * 0.32);
  });
  return (
    <group position={position}>
      <mesh castShadow position={[0, 2.4, 0]}>
        <cylinderGeometry args={[0.07, 0.09, 4.8, 8]} />
        <meshStandardMaterial color="#22262b" metalness={0.6} roughness={0.5} />
      </mesh>
      <mesh castShadow position={[0.7, 4.7, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.05, 0.05, 1.5, 8]} />
        <meshStandardMaterial color="#22262b" metalness={0.6} roughness={0.5} />
      </mesh>
      <mesh ref={bulb} position={[1.4, 4.6, 0]}>
        <sphereGeometry args={[0.16, 16, 16]} />
        <meshStandardMaterial color="#ffd9a0" emissive="#ffb25e" emissiveIntensity={1.2} />
      </mesh>
      <pointLight ref={light} position={[1.4, 4.5, 0]} color="#ffb25e" intensity={7} distance={16} />
    </group>
  );
}

// Rooftop clutter — parapet, AC units, and a water tower on taller buildings.
// Reads as a real skyline instead of flat box-tops.
function Rooftop({ w, h, d, seed = 0, water = false }) {
  const acs = useMemo(() => {
    const n = 1 + (Math.floor(w) % 3);
    return Array.from({ length: n }, (_, i) => ({
      x: -w / 2 + 1 + ((i * 2.3 + seed) % Math.max(1, w - 2)),
      z: -d / 2 + 0.8 + ((i * 1.7 + seed) % Math.max(1, d - 1.6)),
      s: 0.5 + ((i + seed) % 3) * 0.12,
    }));
  }, [w, d, seed]);
  return (
    <group position={[0, h, 0]}>
      {/* Parapet lip around the roof edge */}
      <mesh position={[0, 0.2, -d / 2 + 0.1]}><boxGeometry args={[w, 0.4, 0.2]} /><meshStandardMaterial color="#2a2129" roughness={1} /></mesh>
      <mesh position={[0, 0.2, d / 2 - 0.1]}><boxGeometry args={[w, 0.4, 0.2]} /><meshStandardMaterial color="#2a2129" roughness={1} /></mesh>
      <mesh position={[-w / 2 + 0.1, 0.2, 0]}><boxGeometry args={[0.2, 0.4, d]} /><meshStandardMaterial color="#2a2129" roughness={1} /></mesh>
      <mesh position={[w / 2 - 0.1, 0.2, 0]}><boxGeometry args={[0.2, 0.4, d]} /><meshStandardMaterial color="#2a2129" roughness={1} /></mesh>
      {acs.map((a, i) => (
        <mesh key={i} castShadow position={[a.x, a.s / 2 + 0.1, a.z]}>
          <boxGeometry args={[a.s * 1.6, a.s, a.s * 1.3]} />
          <meshStandardMaterial color="#4a4a52" metalness={0.4} roughness={0.7} />
        </mesh>
      ))}
      {water && (
        <group position={[w / 2 - 1.4, 0, -d / 2 + 1.4]}>
          {[[-0.8, -0.8], [0.8, -0.8], [-0.8, 0.8], [0.8, 0.8]].map(([lx, lz], i) => (
            <mesh key={i} position={[lx, 1, lz]}><cylinderGeometry args={[0.08, 0.08, 2, 6]} /><meshStandardMaterial color="#2b2320" roughness={1} /></mesh>
          ))}
          <mesh castShadow position={[0, 2.6, 0]}><cylinderGeometry args={[1.2, 1.2, 1.8, 14]} /><meshStandardMaterial color="#5a3a2c" roughness={0.95} /></mesh>
          <mesh position={[0, 3.7, 0]}><coneGeometry args={[1.3, 0.9, 14]} /><meshStandardMaterial color="#3a2620" roughness={1} /></mesh>
        </group>
      )}
    </group>
  );
}

// Zig-zag fire escape bolted to a facade — pure street texture/depth.
function FireEscape({ height = 8, floors = 3 }) {
  return (
    <group>
      {Array.from({ length: floors }).map((_, i) => {
        const y = 2.2 + i * (height / (floors + 1));
        return (
          <group key={i} position={[0, y, 0]}>
            <mesh><boxGeometry args={[2.2, 0.08, 1.0]} /><meshStandardMaterial color="#1c1c22" metalness={0.6} roughness={0.6} /></mesh>
            <mesh position={[0, 0.35, 0.5]}><boxGeometry args={[2.2, 0.7, 0.04]} /><meshStandardMaterial color="#1c1c22" metalness={0.6} roughness={0.6} wireframe /></mesh>
            <mesh position={[(i % 2 ? 1 : -1) * 0.9, -0.7, 0.2]} rotation={[0.5, 0, 0]}><boxGeometry args={[0.5, 0.05, 1.4]} /><meshStandardMaterial color="#1c1c22" metalness={0.6} roughness={0.6} /></mesh>
          </group>
        );
      })}
    </group>
  );
}

function Dumpster({ position, color = '#2f5d43' }) {
  return (
    <group position={position}>
      <mesh castShadow position={[0, 0.5, 0]}><boxGeometry args={[1.8, 1.0, 1.1]} /><meshStandardMaterial color={color} roughness={0.85} metalness={0.2} /></mesh>
      <mesh position={[0, 1.02, 0]} rotation={[0.06, 0, 0]}><boxGeometry args={[1.85, 0.08, 1.15]} /><meshStandardMaterial color="#20402f" roughness={0.9} /></mesh>
    </group>
  );
}

// A neon sign — emissive plate + drei text, pulsing on the beat.
function Neon({ position, rotation = [0, 0, 0], text, color, width = 3, height = 0.8, beatPhase = 0 }) {
  const plate = useRef();
  useFrame(() => {
    if (plate.current) plate.current.material.emissiveIntensity = 1.4 + Math.abs(Math.sin(beatPhase * Math.PI * 2)) * 1.0;
  });
  return (
    <group position={position} rotation={rotation}>
      <mesh ref={plate}><planeGeometry args={[width, height]} /><meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.6} toneMapped={false} side={THREE.DoubleSide} /></mesh>
      <Text position={[0, 0, 0.03]} fontSize={height * 0.5} color="#fff8ef" anchorX="center" anchorY="middle" maxWidth={width * 0.95}>
        {text}
      </Text>
    </group>
  );
}

// A building — venue (clickable → interior) or filler. Brick facade facing −z,
// lit windows, rooftop clutter, an awning + neon for venues.
function Building({ x, z, w, h, d, color, name, interiorId, accent = '#ff5e78', beatPhase = 0, night = 0, water = false, awning = false, seed = 0 }) {
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const brickBase = useOptionalTexture('/textures/brick.jpg', brickTexture(3));
  const brick = useMemo(() => withRepeat(brickBase, w / 3.2, h / 3.2), [brickBase, w, h]);
  const cols = Math.max(1, Math.floor(w / 2));
  const rows = Math.max(1, Math.floor(h / 3));
  const front = -(d / 2 + 0.02); // −z facade plane
  return (
    <group position={[x, 0, z]}>
      <mesh
        castShadow
        receiveShadow
        position={[0, h / 2, 0]}
        onClick={interiorId ? (e) => { e.stopPropagation(); enterInterior(interiorId); } : undefined}
        onPointerOver={interiorId ? (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; } : undefined}
        onPointerOut={interiorId ? () => { document.body.style.cursor = 'auto'; } : undefined}
      >
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={color} roughness={0.92} metalness={0.02} map={brick} />
      </mesh>

      {/* Window grid on the road-facing wall */}
      {Array.from({ length: rows }).map((_, r) => (
        Array.from({ length: cols }).map((_, c) => (
          <mesh key={`${r}-${c}`} position={[-w / 2 + 1 + c * 2, 1.8 + r * 3, front]} rotation={[0, Math.PI, 0]}>
            <planeGeometry args={[1, 1.5]} />
            <meshStandardMaterial
              color="#ffd27d"
              emissive="#ff9a3c"
              emissiveIntensity={(0.12 + night * 1.0) * (((r + c + seed) % 4) ? 1 : 0.25)}
              toneMapped={false}
            />
          </mesh>
        ))
      ))}

      {/* Ground-floor storefront glass */}
      <mesh position={[0, 1.0, front]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[Math.min(w - 1, 8), 1.8]} />
        <meshStandardMaterial color="#0e1418" emissive={accent} emissiveIntensity={0.12 + night * 0.5} metalness={0.3} roughness={0.4} />
      </mesh>

      {/* Venue dressing: a modest storefront canopy (muted canvas, accent
          valance) sized to the door — NOT a full-facade slab — sitting above
          the ground-floor glass so it never clips the windows. */}
      {awning && (() => {
        const aw = Math.min(w - 3, 4.6);
        return (
          <group position={[0, 2.5, front - 0.5]}>
            <mesh castShadow rotation={[Math.PI / 2.35, 0, 0]}>
              <planeGeometry args={[aw, 0.95]} />
              <meshStandardMaterial color="#241a20" roughness={0.85} side={THREE.DoubleSide} />
            </mesh>
            {/* front valance stripe glows a little at night */}
            <mesh position={[0, -0.32, 0.34]}>
              <planeGeometry args={[aw, 0.28]} />
              <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.2 + night * 0.7} side={THREE.DoubleSide} toneMapped={false} />
            </mesh>
          </group>
        );
      })()}
      {name && (
        <Neon position={[0, h + 0.7, front - 0.06]} rotation={[0, Math.PI, 0]} text={name} color={accent} width={Math.min(w * 0.85, 7)} height={0.85} beatPhase={beatPhase} />
      )}

      <Rooftop w={w} h={h} d={d} seed={seed} water={water} />
    </group>
  );
}

// A wet alley leading back into the block. Two brick walls + a glossy floor
// (puddle reflections), fire escapes, a dumpster, overhead wires, and the
// destination venue's neon glowing at the far end.
function Alley({ x, width, floorZ0, wallZ0, z1, night = 0, endText, endColor, beatPhase = 0 }) {
  const brickBase = useOptionalTexture('/textures/brick.jpg', brickTexture(3));
  const wallLen = z1 - wallZ0;
  const wallH = 13;
  const leftBrick = useMemo(() => withRepeat(brickBase, wallLen / 3.2, wallH / 3.2), [brickBase, wallLen]);
  const rightBrick = useMemo(() => withRepeat(brickBase, wallLen / 3.2, wallH / 3.2), [brickBase, wallLen]);
  const cz = (wallZ0 + z1) / 2;
  return (
    <group>
      {/* Wet floor — low roughness so it catches the neon (needs an env map to
          truly mirror; even without, the dark gloss reads as damp asphalt). */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.06, (floorZ0 + z1) / 2]}>
        <planeGeometry args={[width, z1 - floorZ0]} />
        <meshStandardMaterial color="#14141a" roughness={0.3} metalness={0.5} envMapIntensity={1.2} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
      </mesh>
      {/* Walls */}
      <mesh castShadow receiveShadow position={[x - width / 2, wallH / 2, cz]}>
        <boxGeometry args={[0.5, wallH, wallLen]} />
        <meshStandardMaterial color="#3a2b31" roughness={1} map={leftBrick} />
      </mesh>
      <mesh castShadow receiveShadow position={[x + width / 2, wallH / 2, cz]}>
        <boxGeometry args={[0.5, wallH, wallLen]} />
        <meshStandardMaterial color="#402f38" roughness={1} map={rightBrick} />
      </mesh>
      {/* Fire escapes on both walls */}
      <group position={[x - width / 2 + 0.4, 0, cz - 2]} rotation={[0, Math.PI / 2, 0]}><FireEscape height={10} floors={3} /></group>
      <group position={[x + width / 2 - 0.4, 0, cz + 2]} rotation={[0, -Math.PI / 2, 0]}><FireEscape height={10} floors={3} /></group>
      {/* Dumpster + crates near the mouth */}
      <Dumpster position={[x - width / 2 + 1.2, 0, wallZ0 + 1.5]} />
      <mesh castShadow position={[x + width / 2 - 1, 0.4, wallZ0 + 1]}><boxGeometry args={[0.9, 0.8, 0.9]} /><meshStandardMaterial color="#6b4a2f" roughness={0.95} /></mesh>
      {/* Overhead wires + string of small warm bulbs (the alley's practical light) */}
      {[0.35, 0.62].map((t, i) => (
        <mesh key={i} position={[x + (i ? -0.6 : 0.6), 9.5, cz]} rotation={[0, 0, 0]}>
          <boxGeometry args={[0.03, 0.03, wallLen]} />
          <meshStandardMaterial color="#111" roughness={1} />
        </mesh>
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <mesh key={i} position={[x, 8.9, wallZ0 + 1 + (i * wallLen) / 8]}>
          <sphereGeometry args={[0.09, 8, 8]} />
          <meshStandardMaterial color="#ffcf8a" emissive="#ffb25e" emissiveIntensity={0.5 + night * 1.4} toneMapped={false} />
        </mesh>
      ))}
      {/* The destination sign glowing at the end of the corridor */}
      {endText && (
        <Neon position={[x, 5.6, z1 - 0.3]} rotation={[0, Math.PI, 0]} text={endText} color={endColor} width={Math.min(width * 0.9, 5)} height={0.9} beatPhase={beatPhase} />
      )}
      <pointLight position={[x, 6, cz]} color={endColor || '#ff9a3c'} intensity={2 + night * 4} distance={14} />
    </group>
  );
}

export default function Street({ beatPhase = 0, energy = 0.5, night = 0 }) {
  // Venue footprints come from the shared layout so map + camera + geometry
  // never drift. Sizes are per-venue; fillers fill the gaps for density.
  const V = useMemo(() => Object.fromEntries(TOWN_LOCATIONS.map((l) => [l.key, l])), []);

  // Filler storefronts (non-enterable) — density between and behind the venues.
  const fillers = useMemo(() => ([
    // front row
    { x: -13, z: 12, w: 6, h: 7, d: 6, color: '#4a3b52', name: 'FADES', accent: '#c58bff', seed: 1, awning: true },
    { x: 4, z: 12, w: 8, h: 10, d: 6, color: '#33212a', name: 'THE CUT', accent: '#ff5e78', seed: 2, water: true },
    { x: 30, z: 13, w: 7, h: 12, d: 6, color: '#2f3b4a', name: 'BODEGA', accent: '#9dff7d', seed: 3, water: true },
    // deep row (seen past the alleys / above the rooflines)
    { x: -20, z: 30, w: 9, h: 16, d: 7, color: '#26222c', seed: 4, water: true },
    { x: 6, z: 32, w: 9, h: 13, d: 7, color: '#241c1e', seed: 5 },
    { x: 13, z: 33, w: 7, h: 11, d: 6, color: '#2a2028', seed: 6, water: true },
    { x: -14, z: 34, w: 8, h: 15, d: 7, color: '#221e26', seed: 7 },
  ]), []);

  // Far background skyline — emissive lit-window slabs for the dusk city glow.
  const skyline = useMemo(() => Array.from({ length: 30 }, (_, i) => ({
    x: -90 + i * 6.2, h: 12 + ((i * 37) % 22), w: 4 + ((i * 13) % 3),
  })), []);
  const windowsBase = useOptionalTexture('/textures/skyline_windows.jpg', null);
  const skylineWindows = useMemo(
    () => skyline.map((b, i) => windowsBase
      ? withRepeat(windowsBase, b.w / 8, b.h / 12)
      : withRepeat(litWindowsTexture(1 + (i % 4), 0.3 + (i % 3) * 0.08), b.w / 3, b.h / 6)),
    [skyline, windowsBase]
  );

  // Ground materials
  const asphaltBase = useOptionalTexture('/textures/asphalt.jpg', null);
  const asphaltMap = useMemo(() => (asphaltBase ? withRepeat(asphaltBase, 30, 10) : null), [asphaltBase]);
  const sidewalkBase = useOptionalTexture('/textures/sidewalk.jpg', null);
  const sidewalkMap = useMemo(() => (sidewalkBase ? withRepeat(sidewalkBase, 60, 2) : null), [sidewalkBase]);

  return (
    <group>
      {/* Ground: the block spans wide in X and deep in Z */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 14]}>
        <planeGeometry args={[240, 80]} />
        <meshStandardMaterial color={asphaltMap ? '#b6b6bc' : PALETTE.asphalt} roughness={1} map={asphaltMap} />
      </mesh>
      {/* Front sidewalk (viewer side of the front row) */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 6]}>
        <planeGeometry args={[240, 7]} />
        <meshStandardMaterial color={sidewalkMap ? '#cfcfd4' : '#33333a'} roughness={1} map={sidewalkMap} polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
      </mesh>
      {/* Main-street center dashes */}
      {Array.from({ length: 26 }).map((_, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[-120 + i * 9.5, 0.08, 1]}>
          <planeGeometry args={[4, 0.25]} />
          <meshStandardMaterial color="#e8c35a" emissive="#e8c35a" emissiveIntensity={0.12} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
        </mesh>
      ))}

      {/* THE FOUR LOCATIONS — spread across the block (positions from TOWN_LOCATIONS) */}
      <Building x={V['studio'].world[0]} z={V['studio'].world[1]} w={10} h={9} d={7} color="#5c4433" name="THE STUDIO" interiorId="studio" accent={V['studio'].accent} beatPhase={beatPhase} night={night} awning water seed={11} />
      <Building x={V['record-store'].world[0]} z={V['record-store'].world[1]} w={9} h={7} d={6} color="#54413a" name="WAX & VINYL" accent={V['record-store'].accent} beatPhase={beatPhase} night={night} awning seed={12} />
      <Building x={V['listening-room'].world[0]} z={V['listening-room'].world[1]} w={8} h={8} d={6} color="#4a3b52" name="LISTENING ROOM" interiorId="listening-room" accent={V['listening-room'].accent} beatPhase={beatPhase} night={night} seed={13} />
      <Building x={V['concert-hall'].world[0]} z={V['concert-hall'].world[1]} w={16} h={14} d={10} color="#2f3b4a" name="CONCERT HALL" interiorId="concert-hall" accent={V['concert-hall'].accent} beatPhase={beatPhase} night={night} water seed={14} />

      {/* Filler storefronts + deep buildings */}
      {fillers.map((b, i) => (
        <Building key={i} {...b} beatPhase={beatPhase} night={night} />
      ))}

      {/* Two alleys cut into the block. Left alley leads to the Listening Room. */}
      <Alley x={-6} width={7} floorZ0={7} wallZ0={15} z1={26} night={night} endText="LISTENING ROOM" endColor={V['listening-room'].accent} beatPhase={beatPhase} />
      <Alley x={12} width={6} floorZ0={7} wallZ0={16} z1={30} night={night} endText="STAGE DOOR" endColor={V['concert-hall'].accent} beatPhase={beatPhase} />

      {/* Far skyline glow */}
      {skyline.map((b, i) => (
        <mesh key={i} position={[b.x, b.h / 2, 48]}>
          <boxGeometry args={[b.w, b.h, 4]} />
          <meshStandardMaterial color="#201a1f" roughness={1} emissive="#ffffff" emissiveIntensity={windowsBase ? 0.03 + night * 0.8 : 0.22 + night * 0.6} emissiveMap={skylineWindows[i]} />
        </mesh>
      ))}

      {/* Streetlights down the main street */}
      {[-34, -14, 8, 26, 40].map((x, i) => (
        <Streetlight key={i} position={[x, 0, 5]} beatPhase={(beatPhase + i * 0.13) % 1} energy={energy} night={night} />
      ))}

      {/* A pocket park at the block's left corner */}
      <Park position={[-42, 0, 16]} beatPhase={beatPhase} />

      {/* Lowrider cruising the main street */}
      <Lowrider beatPhase={beatPhase} energy={energy} position={[0, 0, 1]} />
    </group>
  );
}
